import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Shield, Users, Briefcase } from 'lucide-react';
import { SEO } from '../components/SEO';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HeroIllustration } from '../components/HeroIllustration';
import { StorageVisualization } from '../components/StorageVisualization';
import { IsometricServers } from '../components/IsometricServers';
import { InteractiveServers } from '../components/InteractiveServers';
import { DashboardShowcase } from '../components/DashboardShowcase';

export function HomePage() {
  const { t } = useTranslation();
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: featuresRef, isVisible: featuresVisible } = useScrollAnimation();

  const features = [
    {
      id: 'secure_access',
      icon: Shield,
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      id: 'team_drive',
      icon: Users,
      gradient: 'from-green-500 to-green-600',
    },
    {
      id: 'business_storage',
      icon: Briefcase,
      gradient: 'from-purple-500 to-purple-600',
    },
  ];

  return (
    <>
      <SEO />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background pt-20 pb-8 lg:pt-24 lg:pb-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center -mx-4">
            <div className="w-full px-4 lg:w-5/12">
              <div ref={heroRef} className="hero-content lg:pr-24 xl:pr-32">
                <h1 className={`mb-5 text-3xl font-bold !leading-[1.208] text-foreground sm:text-4xl lg:text-[36px] xl:text-[42px] transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                  {t('home.hero.title')}
                </h1>
                <p className={`mb-8 max-w-[480px] text-base text-foreground/80 transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                  {t('home.hero.subtitle')}
                </p>
                <Link to="/auth/plans" className={`inline-block transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                  <Button size="lg" variant="primary" className="button-hover hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    {t('home.hero.cta')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden px-4 lg:block lg:w-7/12">
              <div className="lg:ml-auto lg:pl-20 xl:pl-24">
                <div className={`relative z-10 transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                  <DashboardShowcase />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-background py-20">
        <div className="container mx-auto px-4">
          <div ref={featuresRef} className="mb-16 text-center">
            <h2 className={`mb-4 text-3xl font-bold text-foreground sm:text-4xl md:text-[40px] transition-all duration-700 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {t('home.services.title')}
            </h2>
            <p className={`text-base text-foreground/80 transition-all duration-700 delay-100 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {t('home.services.subtitle')}
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className={`rounded-lg bg-card p-8 shadow-sm hover-lift card-hover transition-all duration-700 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className={`mb-8 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-r ${feature.gradient} icon-bounce`}>
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="mb-4 text-xl font-semibold text-foreground">
                  {t(`services.${feature.id}.title`)}
                </h3>
                <p className="mb-6 text-base text-foreground/80">
                  {t(`services.${feature.id}.description`)}
                </p>
                <ul className="space-y-2">
                  {Object.keys(t(`services.${feature.id}.features`, { returnObjects: true })).map((key) => (
                    <li key={key} className="flex items-center text-foreground/80">
                      <svg className="mr-2 h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t(`services.${feature.id}.features.${key}`)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-background pt-20 pb-12 md:pb-16 lg:pb-20">
        <div className="container mx-auto px-4">
          <div className="relative rounded-2xl bg-gradient-to-r from-primary/10 to-primary/5 p-8 md:p-12 overflow-hidden animate-on-scroll">
            <div className="absolute inset-0 shimmer opacity-30"></div>
            <div className="max-w-2xl relative z-10">
              <h2 className="mb-4 text-3xl font-bold text-foreground animate-fadeInLeft">
                {t('home.cta.title')}
              </h2>
              <p className="mb-8 text-base text-foreground/80 animate-fadeInLeft" style={{ animationDelay: '0.2s' }}>
                {t('home.cta.subtitle')}
              </p>
              <Link to="/auth/plans" className="inline-block animate-fadeInLeft" style={{ animationDelay: '0.4s' }}>
                <Button size="lg" variant="primary" className="button-hover hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  {t('home.cta.button')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default HomePage;
